import React, { useEffect, useMemo, useState } from 'react';
import { useIntl } from 'umi';
import { getExecutiveSummaryData, getExecutiveSummaryNotes, updateExecutiveSummaryNotes } from '@sg/services/index';
import { IBusinessGrossRevenueCapsule } from '../type';
import { filterFontSizeReviewByKey } from '@sg/utils/commonPageMethod';
import {
  COEFFICIENT,
  BUSINESS_GROSS_REVENUE,
  BUSINESS_PROFIT
} from '../config';
import { ICapsuleData } from "@sg/components/CapsuleBar/type.d";
import MetricCard from '../MetricCard';
import WeeklyProjectHighlight from '../WeeklyProjectHighlight';
import Loading from '@intelligence/components/Loading';
import { messageTips } from '@gp/utils/common';
import { getButtonPermission } from '@sg/_config/permission';
import { usePermissionReady } from '@sg/hooks/usePermissionReady';
import { EDIT_PAGE_KEY_MAP } from '@/pages/strategic/hooks/useAutoSaveParams';
import { buildUpdateTime } from "@sg/utils/common";

export default function BusinessManagement(props: any) {
  const { pageId, forecastCheckbox = [] } = props;
  const { formatMessage } = useIntl();
  usePermissionReady();

  // API data state
  const [metricsData, setMetricsData] = useState<any>(null);

  // Shared state for both RichText components collapse state
  const [isRichTextCollapsed, setIsRichTextCollapsed] = useState(true);
  const [syncIds, setSyncIds] = useState({});
  const [revenueNotes, setRevenueNotes] = useState<string>('');
  const [profitNotes, setProfitNotes] = useState<string>('');
  const [revenueUpdateTime, setRevenueUpdateTime] = useState<string>('');
  const [profitUpdateTime, setProfitUpdateTime] = useState<string>('');

  const fetchNotes = async (type?: 'revenue' | 'profit') => {
    try {
      if (type === 'revenue') {
        const revenueResponse = await getExecutiveSummaryNotes({ type: 'revenue' });
        const filterData = filterFontSizeReviewByKey(revenueResponse.data.note.note);
        setRevenueNotes(filterData);
        setRevenueUpdateTime(revenueResponse.data.datatime);
      } else if (type === 'profit') {
        const profitResponse = await getExecutiveSummaryNotes({ type: 'profit' });
        const filterData = filterFontSizeReviewByKey(profitResponse.data.note.note);
        setProfitNotes(filterData);
        setProfitUpdateTime(profitResponse.data.datatime);
      } else {
        // Fetch both if no type specified
        const [revenueResponse, profitResponse] = await Promise.all([
          getExecutiveSummaryNotes({ type: 'revenue' }),
          getExecutiveSummaryNotes({ type: 'profit' })
        ]);

        setRevenueNotes(revenueResponse.data.note.note);
        setProfitNotes(profitResponse.data.note.note);
        setRevenueUpdateTime(revenueResponse.data.datatime);
        setProfitUpdateTime(profitResponse.data.datatime);
      }
    } catch (error) {
      console.error('Failed to fetch notes:', error);
    }
  };

  const handleSaveNotes = async (type: 'revenue' | 'profit', newData: string) => {
    try {
      // need pass in updateTime?
      await updateExecutiveSummaryNotes({
        type,
        note: newData
      });

      // Fetch the latest notes data after successful update
      await fetchNotes(type);

      messageTips('success', "Operation Successfully");
    } catch (error) {
      console.error(`Failed to save ${type} notes:`, error);
      messageTips('error', "Operation Failure");
    }
  };

  const init = async () => {
    try {
      const response = await getExecutiveSummaryData({});
      // console.log('Response of getExecutiveSummaryData: ', response);
      await fetchNotes();

      const metricsData = response.data;

      // Modify specific values for demo purposes
      // if (metricsData?.profit?.capabilities_supporting) {
      //   metricsData.profit.capabilities_supporting.ytm_actual = 450;
      //   metricsData.profit.capabilities_supporting.full_year_forecast_neutral = 408;
      //   metricsData.profit.capabilities_supporting.full_year_forecast_business = -33;
      // }

      // console.log('Modified metricsData for demo: ', metricsData);

      setMetricsData(metricsData);
    } catch (err) {
      console.error('Failed to fetch executive summary data:', err);
      messageTips('error', 'Failed to load data. Please try again.');
      setMetricsData(null);
    }
  };

  // 初始化
  useEffect(() => {
    init();
  }, []);

  const onSyncGroupChange = (data: IBusinessGrossRevenueCapsule, ids: string[]) => {
    setSyncIds((prev) => ({
      ...prev,
      [data.group || '']: ids || [],
    }));
  };

  const handleRichTextToggle = () => {
    setIsRichTextCollapsed(!isRichTextCollapsed);
  };

  // Function to modify capsule data based on checkbox state
  const changeCapsualBusinessTeamItem = (arr: ICapsuleData[], value: string[] = []) => {
    const newArr: any = [];
    const flag = value?.[0] !== 'businessTeamForecast';
    const findIndex = arr.findIndex((item: ICapsuleData) => String(item?.title).endsWith('F - Business Team'));
    if (findIndex !== -1) {
      arr.forEach((item: any, index) => {
        if (index !== findIndex) {
          newArr.push(item);
        }
      });
      newArr.push({
        ...arr[findIndex],
        isHide: flag,
      });
      return newArr;
    }
    return arr;
  };

  // Function to modify capsule arrays based on checkbox state
  const modifyCapsuleData = (capsuleData: IBusinessGrossRevenueCapsule[]) => {
    return capsuleData.map(item => ({
      ...item,
      metrics: changeCapsualBusinessTeamItem(item.metrics, forecastCheckbox)
    }));
  };

  // Create configs with API data
  const revenueConfig = useMemo(() => {
    const config = BUSINESS_GROSS_REVENUE(metricsData);
    // Apply checkbox modifications to capsule data
    const modifiedConfig = {
      ...config,
      capsule: modifyCapsuleData(config.capsule)
    };
    // console.log('Revenue Config:', modifiedConfig);
    return modifiedConfig;
  }, [metricsData, forecastCheckbox]);

  const profitConfig = useMemo(() => {
    const config = BUSINESS_PROFIT(metricsData);
    // Apply checkbox modifications to capsule data
    const modifiedConfig = {
      ...config,
      capsule: modifyCapsuleData(config.capsule)
    };
    // console.log('Profit Config:', modifiedConfig);
    return modifiedConfig;
  }, [metricsData, forecastCheckbox]);

  return (
    <>
      <div>
        <div className='dbs-home-studio-card'>
          <MetricCard
            key={revenueConfig?.title}
            coefficient={COEFFICIENT}
            config={revenueConfig}
            capsuleMode={'dual'}
            isSharingMaximum={true}
            mode={'receive'}
            syncGroupChange={onSyncGroupChange}
            syncIds={syncIds}
            // Notes
            showRichText={true}
            richTextData={revenueNotes}
            richTextEditable={true}
            onRichTextSave={(newData) => {
              console.log('Notes updated:', newData);
              // Here you can add API call to save the updated notes
              handleSaveNotes('revenue', newData)
            }}
            // Sharing same collapse state
            richTextCollapsed={isRichTextCollapsed}
            onRichTextToggle={handleRichTextToggle}
            richTextUserPermissions={{ canEdit: getButtonPermission('business_totalGrossRevenue_notes') }}
            richTextGroup="executive-notes"
            richTextType="revenue"
            richTextAutoSavePageKey={EDIT_PAGE_KEY_MAP.business_executive_summary_revenue_notes}
            richTextSubtitle={buildUpdateTime(revenueUpdateTime)}
          />
          <MetricCard
            key={profitConfig?.title}
            coefficient={COEFFICIENT}
            config={profitConfig}
            capsuleMode={'dual'}
            isSharingMaximum={true}
            mode={'send'}
            syncGroupChange={onSyncGroupChange}
            syncIds={syncIds}
            // Notes
            showRichText={true}
            richTextData={profitNotes}
            richTextEditable={true}
            onRichTextSave={(newData) => {
              console.log('Notes updated:', newData);
              // Here you can add API call to save the updated notes
              handleSaveNotes('profit', newData)
            }}
            // Sharing same collapse state
            richTextCollapsed={isRichTextCollapsed}
            onRichTextToggle={handleRichTextToggle}
            richTextUserPermissions={{ canEdit: getButtonPermission('business_totalProfit_notes') }}
            richTextGroup="executive-notes"
            richTextType="profit"
            richTextAutoSavePageKey={EDIT_PAGE_KEY_MAP.business_executive_summary_profit_notes}
            richTextSubtitle={buildUpdateTime(profitUpdateTime)}
          />
        </div>
        {/* Weekly Projects Highlight */}
        <WeeklyProjectHighlight
          editable={true}
          userPermissions={{ canEdit: getButtonPermission('business_weeklyProjectHighlight') }}
        />
      </div>
    </>
  );
}
